import { AlertCircle, Check, Info, LoaderCircle, Search, X } from 'lucide-react';
import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { EnrollmentStatus, OlympiadStatus } from '../domain/types';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export function Button({
  variant = 'primary',
  loading = false,
  children,
  className = '',
  disabled,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant; loading?: boolean }) {
  return (
    <button
      className={`button button-${variant} ${className}`}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <LoaderCircle className="spin" aria-hidden="true" size={18} /> : null}
      <span>{children}</span>
    </button>
  );
}

export function IconButton({ label, children, className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { label: string; children: ReactNode }) {
  return (
    <button className={`icon-button ${className}`} aria-label={label} title={label} {...props}>
      {children}
    </button>
  );
}

export function StatusChip({ status }: { status: EnrollmentStatus | OlympiadStatus | 'active' | 'inactive' | 'published' }) {
  const map: Record<string, { icon: string; label: string; className: string }> = {
    open: { icon: '●', label: 'Inscrições abertas', className: 'status-info' },
    upcoming: { icon: '◇', label: 'Em breve', className: 'status-warning' },
    closed: { icon: '■', label: 'Encerrada', className: 'status-neutral' },
    confirmed: { icon: '✓', label: 'Confirmada', className: 'status-success' },
    pending: { icon: '◐', label: 'Pendente', className: 'status-warning' },
    needs_info: { icon: '◇', label: 'Aguardando informação', className: 'status-danger' },
    cancelled: { icon: '×', label: 'Cancelada', className: 'status-neutral' },
    active: { icon: '●', label: 'Ativo', className: 'status-success' },
    inactive: { icon: '○', label: 'Inativo', className: 'status-neutral' },
    published: { icon: '▲', label: 'Publicado', className: 'status-info' },
  };
  const item = map[status] ?? map.closed;
  return <span className={`status-chip ${item.className}`}><span aria-hidden="true">{item.icon}</span>{item.label}</span>;
}

export function Field({ label, hint, error, children }: { label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <label className={`field ${error ? 'field-error' : ''}`}>
      <span className="field-label">{label}</span>
      {children}
      {error ? <span className="field-message" role="alert">{error}</span> : hint ? <span className="field-hint">{hint}</span> : null}
    </label>
  );
}

export function SearchField({ value, onChange, placeholder = 'Buscar...', label = 'Buscar' }: { value: string; onChange: (value: string) => void; placeholder?: string; label?: string }) {
  return (
    <label className="search-field">
      <Search size={18} aria-hidden="true" />
      <span className="sr-only">{label}</span>
      <input type="search" value={value} onChange={(event) => onChange(event.target.value)} placeholder={placeholder} />
      {value ? <button type="button" aria-label="Limpar busca" onClick={() => onChange('')}><X size={16} /></button> : null}
    </label>
  );
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: string; description?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </header>
  );
}

export function EmptyState({ icon, title, description, action }: { icon?: ReactNode; title: string; description: string; action?: ReactNode }) {
  return (
    <section className="empty-state">
      <div className="empty-icon" aria-hidden="true">{icon ?? <Search size={22} />}</div>
      <h2>{title}</h2>
      <p>{description}</p>
      {action}
    </section>
  );
}

export function InlineMessage({ type = 'info', title, children }: { type?: 'success' | 'error' | 'info'; title: string; children?: ReactNode }) {
  const Icon = type === 'success' ? Check : type === 'error' ? AlertCircle : Info;
  return (
    <div className={`inline-message inline-${type}`} role={type === 'error' ? 'alert' : 'status'}>
      <Icon size={19} aria-hidden="true" />
      <div><strong>{title}</strong>{children ? <div>{children}</div> : null}</div>
    </div>
  );
}

export function Skeleton({ rows = 3 }: { rows?: number }) {
  return (
    <div className="skeleton-card" aria-label="Carregando conteúdo" aria-busy="true">
      <span className="skeleton-block skeleton-title" />
      {Array.from({ length: rows }, (_, index) => <span key={index} className="skeleton-block" style={{ width: `${90 - index * 11}%` }} />)}
    </div>
  );
}

export function Dialog({ open, title, description, onClose, children, actions, size = 'medium', closeable = true, centered = false, className = '' }: { open: boolean; title: string; description?: string; onClose: () => void; children: ReactNode; actions?: ReactNode; size?: 'small' | 'medium' | 'large'; closeable?: boolean; centered?: boolean; className?: string }) {
  const titleId = useId();
  const descriptionId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const container = containerRef.current;
    const focusable = container?.querySelector<HTMLElement>('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])');
    focusable?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeable) onCloseRef.current();
      if (event.key !== 'Tab' || !container) return;
      const items = [...container.querySelectorAll<HTMLElement>('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])')];
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
      previous?.focus();
    };
  }, [closeable, open]);

  if (!open) return null;
  return createPortal(
    <div className={`dialog-backdrop${centered ? ' dialog-backdrop-centered' : ''}`} onMouseDown={(event) => { if (closeable && event.target === event.currentTarget) onClose(); }}>
      <div className={`dialog dialog-${size} ${className}`.trim()} role="dialog" aria-modal="true" aria-labelledby={titleId} aria-describedby={description ? descriptionId : undefined} ref={containerRef}>
        <header className="dialog-header">
          <div><h2 id={titleId}>{title}</h2>{description ? <p id={descriptionId}>{description}</p> : null}</div>
          {closeable ? <IconButton label="Fechar" onClick={onClose}><X size={20} /></IconButton> : null}
        </header>
        <div className="dialog-body">{children}</div>
        {actions ? <footer className="dialog-actions">{actions}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}

export function SegmentedControl<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (value: T) => void; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button key={option.value} type="button" className={value === option.value ? 'active' : ''} aria-pressed={value === option.value} onClick={() => onChange(option.value)}>{option.label}</button>
      ))}
    </div>
  );
}
